/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import template from './search.stache';
import can from 'can';
import appContext from 'scripts/app-context';
import cludoService, { cludoConfig } from 'scripts/services/cludo-search';
import deviceUtil from 'scripts/utils/device';
import dtm from 'scripts/services/dtm';

const prohibitedTitlePattern = '- The Official Website';

let filters = new can.Map({
    all: {
        value: cludoConfig.filters.all,
        startIndex: 1,
        items: [],
        total: 0,
    },
    videos: {
        value: cludoConfig.filters.videos,
        startIndex: 1,
        items: [],
        total: 0,
    },
    articles: {
        value: cludoConfig.filters.articles,
        startIndex: 1,
        items: [],
        total: 0,
    },
    photos: {
        value: cludoConfig.filters.photos,
        startIndex: 1,
        items: [],
        total: 0,
    },
});

Component.extend({
    tag: 'search-component',
    template: template,
    viewModel: {
        isLoaded: false,
        emptyResult: false,
        searchError: false,
        filters: filters,
        activeFilter: 'all',
        define: {
            paginationSettings: {
                Type: can.Map,
                Value: can.Map,
            },
            searchInformation: {
                Type: can.Map,
                Value: can.Map,
            },
            searchResultMessage: {
                type: 'string',
                value: '',
            },
            results: {
                Type: can.Map,
                Value: can.Map,
            },
            hideFilter: {
                type: 'boolean',
                value: false,
            },
            getResults: {
                get() {
                    let activeFilter = this.attr('activeFilter');

                    let filter = this.attr('filters').attr(activeFilter),
                        items = filter.attr('items') || null;

                    if (!items) {
                        this.attr('emptyResult', true);
                    } else {
                        this.attr('emptyResult', false);
                    }

                    this.attr('results', items);
                    this.attr('hideFilter', !items || !items.length);

                    return items;
                },
            },
            getRequestString: {
                get() {
                    return this.attr('successfulRequestString');
                },
            },
            getUniqueId: {
                get() {
                    return this.attr('uniqueModuleId');
                },
            },
        },
        displayFilter: function(total) {
            return parseInt(total) > 0;
        },
        getNumberOfResults: function() {
            let type = this.attr('activeFilter'),
                filters = this.attr('filters');

            return filters.attr(type).attr('total');
        },
        onFilterChange: function(event) {
            this.attr('activeFilter', event.payload.value);
            can.route.attr('startIndex', 1);
            can.route.attr('type', event.payload.value);
        },
        setSearchValue: function(event) {
            let searchValue = can.trim($(event.target).val());
            this.attr('queryString', searchValue);
            if (searchValue !== this.attr('queryString')) {
                this.attr('searchResultMessage', '');
            }
        },
        getPrefix: function(link) {
            if (link.indexOf('/players/') != -1) {
                return 'PLAYER PROFILE | ';
            } else if (link.indexOf('/watch/') != -1) {
                return 'WATCH | ';
            }
            return '';
        },
        getTitle: function(title) {
            let prohibitedIndex = title.indexOf(prohibitedTitlePattern);
            if (prohibitedIndex != -1) {
                return title.substring(0, prohibitedIndex);
            }
            return title;
        },
    },
    events: {
        inserted: function() {
            can.route.ready();

            let queryString = can.route.attr('q'),
                startIndex = can.route.attr('startIndex'),
                type =
                    can.route.attr('type') ||
                    this.viewModel.attr('activeFilter');

            if (startIndex && type) {
                can.route.attr({ type: type, startIndex: startIndex });
                this.viewModel.attr({ activeFilter: type });
            }

            if (queryString) {
                this.viewModel.attr('queryString', queryString);
            }

            if (startIndex != 1) {
                let filter = this.viewModel.attr('filters').attr(type);
                filter.attr('startIndex', startIndex);
            }

            this.fetchData();

            appContext.model.bind('hamburger-search', (type, query) => {
                can.route.attr('q', query);
                this.viewModel.attr('queryString', query);
                this.fetchData();
            });

            appContext.model.bind('page-changed', (type, newStartIndex) => {
                can.route.attr('startIndex', newStartIndex);
                let activeFilter = this.viewModel.attr('activeFilter');
                let filter = this.viewModel.attr('filters').attr(activeFilter);
                filter.attr('startIndex', newStartIndex);
                this.fetchData([activeFilter]);
            });

            appContext.model.bind('do-search', (type, query) => {
                can.route.attr('q', query);
                this.viewModel.attr('queryString', query);
                this.fetchData();
            });

            this.viewModel.bind('change', (ev, attr, how, newVal, oldVal) => {
                if (attr == 'activeFilter') {
                    this.viewModel.attr('filters').each(function(filter, key) {
                        filter.attr('startIndex', 1);
                    });
                    this.setPaginationData();
                }
            });
        },
        setPaginationData: function() {
            let activeFilter = this.viewModel.attr('activeFilter');
            let filter = this.viewModel.attr('filters').attr(activeFilter);

            this.viewModel.attr('paginationSettings', {
                startIndex: filter.attr('startIndex'),
                total: filter.attr('total'),
            });
        },
        prepareSearchResultsTextMessage: function() {
            const number = this.viewModel.getNumberOfResults();
            const activeFilter = this.viewModel.attr('activeFilter');
            const query = this.viewModel.attr('successfulRequestString');
            const message =
                number > 0
                    ? `${number} results for ${query} in ${activeFilter}`
                    : `No results found for ${query}. Try refining your search to include the exact word or phrase you are searching for and make sure all words are spelled correctly.`;
            this.viewModel.attr('searchResultMessage', message);
        },
        fetchData: function(types) {
            let googleApiPromises = [],
                startIndex = can.route.attr('startIndex'),
                queryString = this.viewModel.attr('queryString'),
                list = types || ['all', 'videos', 'articles', 'photos'],
                type = can.route.attr('type');

            if (!queryString) {
                return;
            }

            if (!startIndex) {
                can.route.attr('startIndex', 1);
            }

            if (!type) {
                can.route.attr('type', 'all');
            }

            this.viewModel.attr('searchError', false);

            this.viewModel.attr('filters').each((filter, key) => {
                if (list.indexOf(key) != -1) {
                    let filters = filter.attr('value');
                    let data = {
                        query:
                            filters.length > 0
                                ? `${queryString} AND (${filters})`
                                : queryString,
                        operator: 'OR',
                        perPage: Number(cludoConfig.perPage),
                        page: Math.ceil(
                            filter.attr('startIndex') / cludoConfig.perPage,
                        ),
                        responseType: 'JsonObject',
                    };

                    googleApiPromises.push(cludoService.fetch(data, key));
                }
            });

            dtm.trackSearch(queryString);

            Promise.all(googleApiPromises).then(
                (results) => {
                    results.forEach((result) => {
                        if (result && result.data) {
                            let filter = this.viewModel
                                .attr('filters')
                                .attr(result.filter);
                            filter.attr(
                                'items',
                                this.mapDocuments(result.data.TypedDocuments),
                            );
                            filter.attr('total', result.data.TotalDocument);
                        }
                    });

                    if (!this.viewModel.attr('isLoaded')) {
                        this.viewModel.attr('isLoaded', true);
                    }

                    this.viewModel.attr('successfulRequestString', queryString);

                    this.prepareSearchResultsTextMessage();
                    this.setPaginationData();

                    if (deviceUtil.isMobile) {
                        this.element
                            .find('.s-search-bar input.s-input-text')
                            .blur();
                    }
                },
                (error) => {
                    this.viewModel.attr('searchError', true);
                },
            );
        },

        mapDocuments: function(docs) {
            function getPrefix(link) {
                if (link.indexOf('/players/') !== -1) {
                    return 'PLAYER PROFILE | ';
                } else if (link.indexOf('/watch/') !== -1) {
                    return 'WATCH | ';
                }
                return '';
            }

            function getTitle(title) {
                let prohibitedIndex = title.indexOf(prohibitedTitlePattern);
                if (prohibitedIndex !== -1) {
                    return title.substring(0, prohibitedIndex);
                }
                return title;
            }

            function getSearchItemDescription(searchItemDescription) {
                let description = '';

                // get 3 items from highlights or 2 items from values
                let highlights = searchItemDescription.Highlights;
                if (highlights && highlights.length > 0) {
                    for (let i = 0; i < 5 && i < highlights.length; i++) {
                        description = description + ' ' + highlights[i] + '...';
                    }
                } else {
                    for (
                        let i = 0;
                        i < 4 && i < searchItemDescription.Values.length;
                        i++
                    ) {
                        description =
                            description + ' ' + searchItemDescription.Values[i];
                    }
                }

                return description;
            }

            return docs.map((doc) => {
                const fields = doc.Fields;
                const link = fields.Url.Value;
                const docTitle = fields.Title ? fields.Title.Value : '';
                const description = fields.Description
                    ? getSearchItemDescription(fields.Description)
                    : '';
                const image = fields.Image ? fields.Image.Value : '';
                return {
                    title: getPrefix(link) + ' ' + getTitle(docTitle),
                    description,
                    link,
                    image,
                };
            });
        },
        '.s-search-bar .s-clear click': function() {
            this.viewModel.attr('queryString', '');
            can.route.removeAttr('q');
        },
        '.s-search-bar .s-form submit': function(el, ev) {
            ev.preventDefault();
            let queryString = encodeURIComponent(
                this.viewModel.attr('queryString'),
            );
            if (!queryString) {
                return;
            }

            can.route.attr('startIndex', 1);
            let activeFilter = this.viewModel.attr('activeFilter');
            let filter = this.viewModel.attr('filters').attr(activeFilter);
            filter.attr('startIndex', 1);

            can.route.attr('q', queryString);
            this.fetchData();
        },
    },
});
